import { Text } from "@blockdaemon-internal/precision-ui";
import {
  Accordion,
  Alert,
  Box,
  Button,
  Code,
  Container,
  Divider,
  Group,
  Space,
  Stack,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useState } from "react";
import badgePurple from "../../assets/blockdeamon-badge-purple.svg";
import badgeWhite from "../../assets/blockdeamon-badge-white.svg";

interface AppError extends Error {
  code: string;
  details?: Record<string, unknown>;
  timestamp?: string;
}

interface ErrorFallbackProps {
  error: unknown;
  resetError?: () => void;
  eventId?: string;
  source?: "global" | "router" | "widget";
}

export function ErrorFallback({
  error,
  resetError,
  eventId,
  source = "global",
}: ErrorFallbackProps) {
  const [showDetails, setShowDetails] = useState(false);
  const { colorScheme } = useMantineColorScheme();

  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const errorMessage = error instanceof Error ? error.message : String(error);
  const isAppError = error instanceof Error && "code" in error;
  const appError = isAppError ? (error as AppError) : null;

  return (
    <Container
      display="flex"
      style={{ alignItems: "center", justifyContent: "center" }}
      h={source === "global" ? "100vh" : "auto"}
    >
      <Container size="md" py="xl">
        <Stack align="center" spacing={0}>
          {/* Header Section - Logo, Title, Description */}
          <Stack align="center" spacing="md" mb="2rem">
            <Box>
              {colorScheme === "dark" ? (
                <Box component="img" src={badgeWhite} alt="Blockdeamon Badge White" w={60} h={60} />
              ) : (
                <Box
                  component="img"
                  src={badgePurple}
                  alt="Blockdeamon Badge Purple"
                  w={60}
                  h={60}
                />
              )}
            </Box>

            <Stack align="center" spacing="xs" maw={480}>
              <Title order={1} ta="center" size="2rem" fw={600}>
                Something went wrong
              </Title>

              <Text
                ta="center"
                c="gray.9"
                variant="body"
                sx={{
                  lineHeight: 1.5,
                }}
              >
                We encountered an unexpected error.
                <br />
                Our team has been automatically notified.
              </Text>
            </Stack>
          </Stack>

          {/* Error Details Section */}
          {appError && (
            <Container size="sm" mb="2rem">
              <Alert color="red" title="Error Details" w="100%">
                <Stack spacing="xs">
                  <Text size="sm">
                    <strong>Code:</strong> {appError.code}
                  </Text>
                  <Text size="sm">{appError.message}</Text>
                </Stack>
              </Alert>
            </Container>
          )}

          {/* Actions Section */}
          <Stack align="center" spacing="xl" w="100%" maw={400}>
            {/* Primary Actions */}
            <Group position="center" spacing="lg" w="100%">
              <Button onClick={handleRefresh} size="md" style={{ minWidth: "120px" }}>
                Try Again
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
                size="md"
                style={{ minWidth: "120px" }}
              >
                Reload Page
              </Button>
            </Group>

            {/* Divider */}
            <Divider
              w="60%"
              sx={(theme) => ({
                borderColor:
                  theme.colorScheme === "dark" ? theme.colors.gray[6] : theme.colors.gray[3],
              })}
            />

            {/* Secondary Actions */}
            <Group position="center" spacing="md">
              <Button size="sm" onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? "Hide" : "Show"} Technical Details
              </Button>
            </Group>
          </Stack>

          {/* Technical Details Section */}
          {showDetails && (
            <>
              <Space h="xl" />
              <Container size="sm" w="100%">
                {eventId ? (
                  /* Sentry Error Display */
                  <Stack spacing="md" align="center">
                    <Text size="sm" fw={500} ta="center" c="gray.4">
                      Technical Information
                    </Text>
                    <Code
                      block
                      c="red"
                      fz="xs"
                      p="lg"
                      w="100%"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: "200px",
                        overflow: "auto",
                      }}
                    >
                      {errorMessage}
                    </Code>
                    <Text size="xs" color="gray.4" ta="center" fw={500}>
                      Error ID:{" "}
                      <Box component="span" ff="monospace">
                        {eventId}
                      </Box>
                    </Text>
                  </Stack>
                ) : (
                  /* Router Error Display */
                  <Accordion variant="contained" w="100%">
                    <Accordion.Item value="error-details">
                      <Accordion.Control>
                        <Text size="sm">Technical Details</Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Code
                          block
                          c="red"
                          fz="xs"
                          p="md"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: "200px",
                            overflow: "auto",
                          }}
                        >
                          {errorMessage}
                        </Code>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )}
              </Container>
            </>
          )}

          {/* Footer Section */}
          <Space h="2rem" />
          <Container size="sm">
            <Text
              variant="body"
              c="gray.9"
              ta="center"
              size="sm"
              sx={{
                lineHeight: 1.4,
              }}
            >
              If this problem persists, please contact support
              <br />
              and reference the error details above.
            </Text>
          </Container>
        </Stack>
      </Container>
    </Container>
  );
}
